import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { computeSpotRate, type SpotConfidence } from "@/lib/spotRateEngine";

/**
 * LR Direct Load Repricing scan.
 *
 * LR Direct-native: reads ONLY direct_loads. Reuses the shared, hub-agnostic
 * rate util (src/lib/spotRateEngine.ts) — no imports from src/components/ai-post/*.
 *
 * Read-only: this scan never writes. Suggestions are advisory; the shipper edits
 * the target on the load itself.
 */

const MAX_POSTS = 50;
const BATCH_SIZE = 5;
const TIMEOUT_MS = 15_000;
/** Flag when the shipper's target RPM is more than 8% under market RPM. */
const UNDER_MARKET_FACTOR = 0.92;

export interface RepricingPost {
  id: string;
  loadNumber: string;
  postType: string | null;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
  totalMiles: number;
  /** Current target the shipper set (midpoint when a range). */
  currentTarget: number;
  currentRpm: number;
  suggestedTarget: number;
  suggestedRpm: number;
  /** suggestedTarget - currentTarget */
  delta: number;
  marketRpm: number;
  tierLabel: string;
  compCount: number;
  confidence: SpotConfidence;
  bidDeadline: string | null;
  hoursToDeadline: number | null;
}

export interface RepricingScanResult {
  scanned: number;
  /** Open posts skipped because no target rate was set. */
  noTarget: number;
  offMarket: RepricingPost[];
  totalUplift: number;
  closingSoon: number;
}

/** Target value used for the comparison: midpoint of the range, or whichever bound exists. */
export function resolveTargetRate(min: number | null, max: number | null): number | null {
  const lo = min != null && Number(min) > 0 ? Number(min) : null;
  const hi = max != null && Number(max) > 0 ? Number(max) : null;
  if (lo != null && hi != null) return (lo + hi) / 2;
  return lo ?? hi;
}

export function useLRDirectRepricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RepricingScanResult | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const abortedRef = useRef(false);

  const scan = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    setProgress({ done: 0, total: 0 });
    abortedRef.current = false;

    const found: RepricingPost[] = [];
    let scanned = 0;
    let noTarget = 0;
    let closingSoon = 0;

    const finish = () => {
      found.sort((a, b) => b.delta - a.delta);
      setResult({
        scanned,
        noTarget,
        offMarket: [...found],
        totalUplift: found.reduce((s, p) => s + p.delta, 0),
        closingSoon,
      });
      setLoading(false);
    };

    const timeoutId = setTimeout(() => {
      abortedRef.current = true;
      finish();
    }, TIMEOUT_MS);

    try {
      const { data, error: qErr } = await supabase
        .from("direct_loads")
        .select(
          "id, load_number, post_type, status, bid_deadline, " +
            "origin_city, origin_state, origin_latitude, origin_longitude, " +
            "destination_city, destination_state, destination_latitude, destination_longitude, " +
            "total_miles, equipment_type, target_rate_min, target_rate_max"
        )
        .eq("created_by", user.id)
        .in("status", ["posted", "bidding"])
        .is("deleted_at", null)
        .not("origin_latitude", "is", null)
        .not("destination_latitude", "is", null)
        .gt("total_miles", 0)
        .order("posted_at", { ascending: false })
        .limit(MAX_POSTS);

      if (qErr) throw qErr;

      const rows = (data ?? []) as any[];
      scanned = rows.length;

      const withTarget = rows.filter((r) => {
        const t = resolveTargetRate(r.target_rate_min, r.target_rate_max);
        if (t == null) {
          noTarget += 1;
          return false;
        }
        return true;
      });

      const now = Date.now();
      for (const r of rows) {
        if (r.bid_deadline) {
          const h = (new Date(r.bid_deadline).getTime() - now) / 3_600_000;
          if (h > 0 && h <= 24) closingSoon += 1;
        }
      }

      if (!withTarget.length) {
        clearTimeout(timeoutId);
        finish();
        return;
      }

      setProgress({ done: 0, total: withTarget.length });

      for (let i = 0; i < withTarget.length; i += BATCH_SIZE) {
        if (abortedRef.current) break;
        const batch = withTarget.slice(i, i + BATCH_SIZE);

        const results = await Promise.allSettled(
          batch.map(async (r) => {
            const miles = Number(r.total_miles);
            const target = resolveTargetRate(r.target_rate_min, r.target_rate_max)!;

            const spot = await computeSpotRate(
              {
                originLat: Number(r.origin_latitude),
                originLon: Number(r.origin_longitude),
                destLat: Number(r.destination_latitude),
                destLon: Number(r.destination_longitude),
                laneMiles: miles,
                equipmentType: r.equipment_type,
                originCity: r.origin_city ?? undefined,
                originState: r.origin_state ?? undefined,
                destCity: r.destination_city ?? undefined,
                destState: r.destination_state ?? undefined,
              },
              { skipTrend: true }
            );

            if (!spot) return null;

            const currentRpm = target / miles;
            if (currentRpm >= spot.marketRpm * UNDER_MARKET_FACTOR) return null;

            const suggestedTarget = Math.round(spot.marketRate);
            const hours = r.bid_deadline
              ? (new Date(r.bid_deadline).getTime() - Date.now()) / 3_600_000
              : null;

            return {
              id: r.id,
              loadNumber: r.load_number,
              postType: r.post_type ?? null,
              originCity: r.origin_city,
              originState: r.origin_state,
              destCity: r.destination_city,
              destState: r.destination_state,
              totalMiles: miles,
              currentTarget: Math.round(target),
              currentRpm: Math.round(currentRpm * 100) / 100,
              suggestedTarget,
              suggestedRpm: Math.round((suggestedTarget / miles) * 100) / 100,
              delta: suggestedTarget - Math.round(target),
              marketRpm: spot.marketRpm,
              tierLabel: spot.tierLabel,
              compCount: spot.compCount,
              confidence: spot.confidence,
              bidDeadline: r.bid_deadline ?? null,
              hoursToDeadline: hours == null ? null : Math.round(hours * 10) / 10,
            } as RepricingPost;
          })
        );

        for (const res of results) {
          if (res.status === "fulfilled" && res.value) found.push(res.value);
        }
        if (!abortedRef.current) {
          setProgress({ done: Math.min(i + BATCH_SIZE, withTarget.length), total: withTarget.length });
        }
      }

      if (!abortedRef.current) {
        clearTimeout(timeoutId);
        finish();
      }
    } catch (e: any) {
      console.error("LR Direct repricing scan failed:", e);
      clearTimeout(timeoutId);
      if (!abortedRef.current) {
        setError(e?.message ?? "Scan failed");
        setLoading(false);
      }
    }
  }, [user]);

  return { scan, loading, result, progress, error, hasScanned: result !== null };
}
